import { useRef, useEffect, useMemo, useState } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { useAuthStore } from '@/stores/auth-store'
import { useSharePointGate } from '@/hooks/useSharePointGate'
import { SharePointBrowser } from '@/components/SharePointBrowser'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FilterDropdown, type FilterOption } from '@/components/ui/FilterDropdown'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  DOCUMENT_CATEGORY_LABELS,
  type DocumentCategory,
} from '@/lib/schemas'
import { cn } from '@/lib/utils'
import {
  Search,
  Plus,
  Upload,
  FolderPlus,
  FolderOpen,
  ChevronDown,
  Shield,
  X,
} from 'lucide-react'
import { SourceIcon } from '@/components/ui/source-icon'

interface LibraryToolbarProps {
  /** Opens the system file picker. From lifted useDropZoneUpload. */
  onOpenFilePicker: () => void
  /** Opens the system folder picker. From lifted useDropZoneUpload. */
  onOpenFolderPicker?: (() => void) | undefined
  /** When false, Upload items are disabled (no folder selected). */
  canUpload: boolean
  /** Opens the Create Root Folder dialog. */
  onNewFolder: () => void
}

export function LibraryToolbar({
  onOpenFilePicker,
  onOpenFolderPicker,
  canUpload,
  onNewFolder,
}: LibraryToolbarProps) {
  const {
    searchQuery,
    setSearchQuery,
    folders,
    categoryFilter,
    toggleCategoryFilter,
    clearAllFilters,
    hasActiveFilters,
    tags,
    selectedTagIds,
    toggleTagFilter,
    clearTagFilter,
    hideArchived,
    setHideArchived,
    factBankFilter,
    setFactBankFilter,
    expiringFilter,
    setExpiringFilter,
  } = useLibraryStore()
  const isViewer = useAuthStore((s) => s.role === 'viewer')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Derive category options from current folders so we never show empty filter values.
  // Folder type, uploader, and free-text filters were dropped in favour of
  // search + folder navigation — see commit message for rationale.
  const categoryOptions: FilterOption[] = useMemo(() => {
    const present = new Set<DocumentCategory>()
    for (const folder of folders) present.add(folder.category)
    return Array.from(present)
      .map((c) => ({ value: c, label: DOCUMENT_CATEGORY_LABELS[c] }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [folders])

  const tagOptions: FilterOption[] = useMemo(() => {
    return tags
      .map((t) => ({ value: t.id, label: t.name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [tags])

  const showFilters = !isViewer || hasActiveFilters()

  // SharePoint integration state
  const { isConnected, ensureChecked, openOrConnect } = useSharePointGate()
  const refreshLibrary = useLibraryStore((s) => s.fetchDocuments)
  const [browserOpen, setBrowserOpen] = useState(false)
  const selectedFolderId = useLibraryStore((s) => s.selectedFolderId)

  // Probe connection once when the toolbar mounts (cheap; cached after).
  useEffect(() => {
    ensureChecked()
  }, [ensureChecked])

  const handleClickImportFromSharePoint = () => {
    openOrConnect(() => setBrowserOpen(true))
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Main row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search names and contents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-12 h-8"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>

        {/* Category filter */}
        {showFilters && (
          <FilterDropdown
            label="Category"
            options={categoryOptions}
            selected={categoryFilter as Set<string>}
            onToggle={(v) => toggleCategoryFilter(v as DocumentCategory)}
            onClear={() => {
              for (const v of categoryFilter) toggleCategoryFilter(v)
            }}
            className="h-8"
          />
        )}

        {/* Tag filter — only visible when the org has at least one tag */}
        {showFilters && tagOptions.length > 0 && (
          <FilterDropdown
            label="Tags"
            options={tagOptions}
            selected={selectedTagIds}
            onToggle={(v) => toggleTagFilter(v)}
            onClear={clearTagFilter}
            className="h-8"
          />
        )}

        {/* Fact Bank lens — show only Fact Bank members */}
        {!isViewer && (
          <Button
            variant={factBankFilter ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setFactBankFilter(!factBankFilter)}
            className={cn('h-8', factBankFilter && 'text-[hsl(var(--brand))]')}
            title="Show only Fact Bank documents"
          >
            <Shield className="h-3.5 w-3.5 mr-1.5" />
            Fact Bank
          </Button>
        )}

        {/* Hide-archived toggle — defaults ON; non-viewers can flip it OFF */}
        {!isViewer && (
          <Button
            variant={hideArchived ? 'outline' : 'secondary'}
            size="sm"
            onClick={() => setHideArchived(!hideArchived)}
            className="h-8"
            title={hideArchived ? 'Show archived documents' : 'Hide archived documents'}
          >
            {hideArchived ? 'Hide archived' : 'Showing archived'}
          </Button>
        )}

        <div className="flex-1" />

        {/* Right-side actions — hidden for viewers */}
        {!isViewer && (
          <>
            <Button variant="ghost" size="sm" className="h-8" onClick={onNewFolder}>
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add
                  <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={onOpenFilePicker} disabled={!canUpload}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload files
                </DropdownMenuItem>
                {onOpenFolderPicker && (
                  <DropdownMenuItem onClick={onOpenFolderPicker} disabled={!canUpload}>
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Upload folder
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleClickImportFromSharePoint}>
                  <SourceIcon source="sharepoint" size={16} className="mr-2" />
                  {isConnected ? 'Import from SharePoint' : 'Connect SharePoint'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* SharePoint import dialog. Lazy-mounted so the picker UI cost is
          deferred until the user actually clicks. */}
      {browserOpen && (
        <SharePointBrowser
          open={browserOpen}
          onClose={() => setBrowserOpen(false)}
          mode="pick"
          libraryFolders={folders}
          defaultLibraryFolderId={selectedFolderId}
          onSuccess={() => {
            void refreshLibrary(selectedFolderId)
          }}
        />
      )}

      {/* Active-filter chip rail — renders whenever any filter is active so
          the visible state of "what's filtering this view" is consistent.
          Each chip carries an X for one-click removal (the trigger's count
          badge can't be clicked away).

          Gated on `hasActiveFilters()`, the store's own definition, rather than
          a hand-listed subset. It used to check category and expiring only, so
          a tag or Fact Bank narrowed the list with no chip — and when one of
          them was the ONLY filter, the whole rail vanished, taking `Clear all`
          with it. `hasActiveFilters` deliberately excludes `hideArchived`: it
          defaults ON, nobody chose it, and its own toggle reads out its state
          ("Hide archived" / "Showing archived"). */}
      {hasActiveFilters() && (
        <ActiveFilterChips
          categoryFilter={categoryFilter}
          onToggleCategory={toggleCategoryFilter}
          expiringFilter={expiringFilter}
          onClearExpiring={() => setExpiringFilter(false)}
          tags={tags}
          selectedTagIds={selectedTagIds}
          onToggleTag={toggleTagFilter}
          factBankFilter={factBankFilter}
          onClearFactBank={() => setFactBankFilter(false)}
          onClearAll={clearAllFilters}
        />
      )}
    </div>
  )
}

// ============================================================================
// Active filter chips — rendered as a second row when 2+ filters are active.
// ============================================================================

interface ActiveFilterChipsProps {
  categoryFilter: Set<DocumentCategory>
  onToggleCategory: (c: DocumentCategory) => void
  expiringFilter: boolean
  onClearExpiring: () => void
  tags: Array<{ id: string; name: string }>
  selectedTagIds: Set<string>
  onToggleTag: (id: string) => void
  factBankFilter: boolean
  onClearFactBank: () => void
  onClearAll: () => void
}

function ActiveFilterChips({
  categoryFilter,
  onToggleCategory,
  expiringFilter,
  onClearExpiring,
  tags,
  selectedTagIds,
  onToggleTag,
  factBankFilter,
  onClearFactBank,
  onClearAll,
}: ActiveFilterChipsProps) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="active-filter-chips">
      {Array.from(categoryFilter).map((c) => (
        <FilterChip
          key={`cat-${c}`}
          label={DOCUMENT_CATEGORY_LABELS[c]}
          prefix="Category"
          onRemove={() => onToggleCategory(c)}
        />
      ))}
      {/* A selection the tag list cannot name still gets a chip, labelled
          rather than skipped. Skipping would put back exactly the bug this rail
          exists to prevent — a filter narrowing the list with nothing to show
          for it — and railing the bare id would print a UUID, which is the
          Analysis owner filter's lesson. The list can be mid-load, and the id
          outlives the tag. */}
      {Array.from(selectedTagIds).map((id) => (
        <FilterChip
          key={`tag-${id}`}
          label={tags.find((t) => t.id === id)?.name ?? 'Unknown'}
          prefix="Tag"
          onRemove={() => onToggleTag(id)}
        />
      ))}
      {factBankFilter && <FilterChip label="Fact Bank" onRemove={onClearFactBank} />}
      {expiringFilter && (
        <FilterChip
          label="Expiring / expired"
          onRemove={onClearExpiring}
        />
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-7 px-2 text-meta hover:text-foreground"
      >
        Clear all
      </Button>
    </div>
  )
}

function FilterChip({
  label,
  prefix,
  onRemove,
}: {
  label: string
  prefix?: string
  onRemove: () => void
}) {
  return (
    <div className="inline-flex items-center h-7 pl-2 pr-1 rounded-md bg-muted text-xs text-foreground border border-border">
      {prefix && <span className="text-muted-foreground mr-1">{prefix}:</span>}
      <span className="font-medium">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${prefix ? `${prefix} ` : ''}${label} filter`}
        className="ml-1 p-0.5 rounded hover:bg-foreground/10 transition-colors"
      >
        <X className="h-3 w-3 text-muted-foreground" />
      </button>
    </div>
  )
}
